import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

// Server-side admin check. The result of this hook decides whether the
// console renders admin UI, but it grants zero data access on its own — every
// admin endpoint independently gates on requireAdmin() server-side.
//
// State machine:
//   loading            first call in flight
//   admin              is_admin returned true
//   not_admin          authenticated but not on the admins allowlist
//   error              network / RPC failure — treat as not_admin, show retry

export type AdminGate =
  | { status: 'loading' }
  | { status: 'admin' }
  | { status: 'not_admin' }
  | { status: 'error'; message: string };

export function useAdminGate(userId: string | null | undefined): AdminGate {
  const [gate, setGate] = useState<AdminGate>({ status: 'loading' });

  useEffect(() => {
    if (!userId) return; // wait for a session
    let cancelled = false;
    setGate({ status: 'loading' });
    supabase.functions
      .invoke<{ admin: boolean }>('admin-check', { body: {} })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setGate({ status: 'error', message: error.message });
          return;
        }
        setGate({ status: data?.admin ? 'admin' : 'not_admin' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setGate({
          status: 'error',
          message: err instanceof Error ? err.message : 'Admin check failed.',
        });
      });
    return () => { cancelled = true; };
  }, [userId]);

  return gate;
}
